/**
 * SAY MY NAME — the entry point.
 *
 * A working entry point, not a homepage. ONE integrated full-height experience,
 * split asymmetrically: the declarative emerald field on the left (68%), and on
 * the right (32%) the near-black agent panel — the visible presence of the naming
 * agent, carrying the status light and the whole interaction. Nothing the visitor
 * needs sits underneath the green.
 *
 * FULL BLEED: the stage centres surfaces in a padded column. This screen escapes
 * it deliberately — the negative margins cancel the stage's page padding
 * (`px-5 md:px-20 pt-8 pb-48` in StageShell) and `--container-content: 100%`
 * (theme.css) opens the column — so the green truly runs to the viewport edges
 * and the two halves meet as one product surface, not as a floating card.
 *
 * The panel's input is REAL: it sends the visitor's own words as a new turn
 * through `useSendIntent`, exactly as the composer would. It is not an echo of
 * the composer placeholder and not a decorative field.
 *
 * Every word here is fixed brand copy owned by this component, so the contract
 * carries no props and the model cannot rewrite the headline.
 */
import { useEffect, type FC } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';
import { useSendIntent } from '@/app/lib/a2ui/blocks/use-send-intent.ts';
import { AgentInput } from './AgentInput.tsx';
import { StatusLight } from './StatusLight.tsx';
import { PhaseIndicator } from '../PhaseIndicator.tsx';
import { markStudioEngaged, resetStudioPhase, statusLightFor } from '../studio-state.ts';

const messages = defineMessages({
  headlineLine1: {
    id: 'studioEntry.headline.line1',
    defaultMessage: 'FIND A',
    description: 'First line of the landing headline',
  },
  headlineLine2: {
    id: 'studioEntry.headline.line2',
    defaultMessage: 'NAME WORTH',
    description: 'Second line of the landing headline',
  },
  headlineLine3: {
    id: 'studioEntry.headline.line3',
    defaultMessage: 'BUILDING ON.',
    description: 'Third line of the landing headline',
  },
  promise: {
    id: 'studioEntry.promise',
    defaultMessage:
      'Not another name generator. A naming strategist that learns what you’re building, explores beyond the obvious, and researches what deserves to survive.',
    description: 'Supporting copy under the landing headline',
  },
  agentLabel: {
    id: 'studioEntry.agentLabel',
    defaultMessage: 'YOUR AGENT',
    description: 'Label identifying the agent panel on the landing screen',
  },
  prompt: {
    id: 'studioEntry.prompt',
    defaultMessage: 'What are we naming?',
    description: 'The first question the agent asks',
  },
  company: {
    id: 'studioEntry.subject.company',
    defaultMessage: 'Company',
    description: 'Subject choice: naming a company',
  },
  product: {
    id: 'studioEntry.subject.product',
    defaultMessage: 'Product',
    description: 'Subject choice: naming a product',
  },
  service: {
    id: 'studioEntry.subject.service',
    defaultMessage: 'Service',
    description: 'Subject choice: naming a service',
  },
  somethingElse: {
    id: 'studioEntry.subject.somethingElse',
    defaultMessage: 'Something else',
    description: 'Subject choice: naming something other than a company, product or service',
  },
  orTellMe: {
    id: 'studioEntry.orTellMe',
    defaultMessage: 'Or just tell me about it',
    description: 'Alternative to picking a subject: describe the thing instead',
  },
  inputPlaceholder: {
    id: 'studioEntry.inputPlaceholder',
    defaultMessage: 'I’m building…',
    description: 'Placeholder of the open-ended input inside the agent panel',
  },
  microcopy: {
    id: 'studioEntry.microcopy',
    defaultMessage: 'The agent will ask only what it needs before it starts working.',
    description: 'Reassurance under the subject choices',
  },
});

const SUBJECTS = [
  { id: 'company', message: messages.company },
  { id: 'product', message: messages.product },
  { id: 'service', message: messages.service },
  { id: 'something-else', message: messages.somethingElse },
] as const;

export const StudioEntry: FC<A2uiNodeViewProps> = () => {
  const intl = useIntl();
  const dispatch = useSurfaceAction();
  const sendIntent = useSendIntent();

  // The landing is always the start of the process — and it is also where the
  // studio is entered, which is what keeps the global composer dock hidden for
  // the rest of the session.
  useEffect(() => {
    markStudioEngaged();
    resetStudioPhase();
  }, []);

  return (
    <div
      data-studio-state="understand"
      className="-mx-5 -mb-48 -mt-8 flex flex-col overflow-x-hidden md:-mx-20 lg:min-h-[calc(100svh-3.25rem)] lg:flex-row lg:items-stretch"
    >
      {/* The green field: the promise, in black type, and nothing else. The one
          approved surface gradient, generous negative space, no other decoration. */}
      <div className="canvas-gradient flex min-w-0 flex-col justify-center bg-field px-7 py-20 text-field-ink sm:px-12 sm:py-28 lg:basis-[68%] lg:px-20 lg:py-32">
        <h1 className="text-[2.75rem] font-bold leading-[0.9] tracking-[-0.02em] text-balance sm:text-6xl lg:text-7xl xl:text-8xl">
          <span className="block">{intl.formatMessage(messages.headlineLine1)}</span>
          <span className="block">{intl.formatMessage(messages.headlineLine2)}</span>
          <span className="block">{intl.formatMessage(messages.headlineLine3)}</span>
        </h1>
        <p className="mt-10 max-w-[38ch] text-base leading-relaxed text-field-ink/80 sm:text-lg">
          {intl.formatMessage(messages.promise)}
        </p>
      </div>

      {/* The agent panel: presence, and the entire interaction. */}
      <div className="flex min-w-0 flex-col gap-7 bg-panel px-7 py-12 pb-32 text-panel-ink sm:px-10 lg:basis-[32%] lg:px-10 lg:py-14">
        <div className="flex items-start gap-2.5">
          <StatusLight status={statusLightFor('understand')} />
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-6 gap-y-3">
            <span
              className="inline-flex h-7 flex-none items-center text-[10px] font-medium tracking-caps text-muted-foreground"
              translate="no"
            >
              {intl.formatMessage(messages.agentLabel)}
            </span>
            <PhaseIndicator />
          </div>
        </div>

        <p className="text-xl font-medium leading-snug tracking-tight text-panel-ink sm:text-2xl">
          {intl.formatMessage(messages.prompt)}
        </p>

        <div className="flex flex-col gap-2">
          {SUBJECTS.map((subject) => (
            <button
              key={subject.id}
              type="button"
              onClick={() => dispatch?.('chooseSubject', { subject: subject.id })}
              /* Interaction outlines are WHITE, never green: green is the brand
                 and the canvas, not the hover colour. Default is a subtle dark
                 grey border; hover and keyboard focus both go white. */
              className="touch-manipulation rounded-sm border border-panel-line bg-transparent px-4 py-3 text-left text-sm font-medium text-panel-ink transition-colors duration-150 hover:border-panel-ink hover:bg-panel-ink/[0.04] focus-visible:border-panel-ink focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-[3px] focus-visible:outline-panel-ink/60 active:scale-[0.99]"
            >
              {intl.formatMessage(subject.message)}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-3 border-t border-panel-line pt-6">
          <p className="text-sm text-panel-ink">{intl.formatMessage(messages.orTellMe)}</p>
          <AgentInput
            placeholder={intl.formatMessage(messages.inputPlaceholder)}
            onSubmit={sendIntent}
          />
          {/* Directly beneath the input, where it answers the hesitation a
              visitor actually has at that moment. */}
          <p className="text-xs leading-relaxed text-muted-foreground-subtle">
            {intl.formatMessage(messages.microcopy)}
          </p>
        </div>
      </div>
    </div>
  );
};
